<script>
import CockpitMatryoshkaColumn from '@/components/cockpit/matryoshka/CockpitMatryoshkaColumn.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import capitalize from '@/utils/capitalize';

export default {
  name: 'CockpitMatryoshka',
  props: {
    selectedLineId: Number,
    linesCollapsed: Boolean,
    activeOrder: Object,
    activeOrders: Array,
    debugStatuses: Array,
  },
  data: () => ({
    loading: false,
    silentLoadingLabelArchive: false,
    silentLoadingEmptyLabelArchive: false,
    labelArchiveError: false,
    emptyLabelArchiveError: false,
    labelArchive: [],
    matryoshkaRefreshInterval: null,
    refreshMaxInterval: 400,
    requestResponseTime: 0,
    lastResponseTimes: [],
    timeInThePast: 0,
    renderTime: 0,
    connected: false,
    stopRefresh: false,

    refreshStatusFull: false,
    refreshStatusHide: false,
    refreshStatusAnimation: true,
    showPerformanceModal: false,
  }),
  emits: [
    'select-item',
    'print-manually',
    'show-print-batch',
  ],
  components: {
    CockpitMatryoshkaColumn,
  },
  computed: {
    ...mapState({
      packingSettings: state => state.packingSettings.packingSettings,
      labelingPoints: state => state.labelingPoints.list || [],
    }),
    ...mapState('debug', ['debug']),
    ...mapGetters([
      'globalSettings',
    ]),
    ...mapGetters('packingSettings', [
      'getUnitsForSku',
      'getUnitToUnitRelation',
      'getLowerLevelUnit',
      'getHigherLevelUnit',
    ]),
    ...mapGetters('debug', ['debug']),
    columns() {
      if (!this.activeOrder) return [];
      return this.getUnitsForSku(this.activeOrder.order.skuId).map(unit => ({
        title: capitalize(unit.name),
        queueLength: Math.ceil(Math.random() * 10),
        palletsColumn: !!this.getLowerLevelUnit(unit.id, this.activeOrder.order.skuId)
        && !this.getHigherLevelUnit(unit.id, this.activeOrder.order.skuId),
        firstColumn: !this.getLowerLevelUnit(unit.id, this.activeOrder.order.skuId)
        && this.getHigherLevelUnit(unit.id, this.activeOrder.order.skuId),
        unit,
      }));
    },
    middleColumnsFetchParams() {
      return this.columns.filter(column => !column.firstColumn && !column.palletsColumn).map(column => ({
        plantCode: this.globalSettings.plantCode,
        query: {
          orderId: this.activeOrder.order.id,
          unitId: column.unit.id,
          labelStatus: 'Active',
        },
      }));
    },
    fetchParams() {
      const { plantCode } = this.globalSettings;
      let labelStatuses = [
        'ACTIVE',
      ];

      if (this.debug) {
        labelStatuses = [
          ...this.debugStatuses,
        ];
      }

      return this.columns.filter(column => column.firstColumn || column.palletsColumn).map(column => ({
        plantCode,
        query: {
          skuId: this.activeOrder.order.skuId,
          orderId: this.activeOrder.order.id,
          lineId: this.selectedLineId,
          unitId: column.unit.id,
          labelStatuses: column.palletsColumn ? [
            'ACTIVE',
            'Aviso',
          ] : labelStatuses,
          onlyWithoutContainers: !!column.firstColumn,
          onlyWithUnlockedParentRelations: !column.firstColumn && !column.palletsColumn,
          from: this.activeOrderDateFrom,
        },
      }));
    },
    amountInQualityControl() {
      return this.labelArchive.filter(label => label.labelStatus === 'QualityCheck').length;
    },
    amountInStorage() {
      return this.labelArchive.filter(label => label.labelStatus === 'StorageSample').length;
    },
    lastColumnUnit() {
      return this.columns[this.columns.length - 1].unit;
    },
    openPalletItemsCount() {
      const prevUnit = this.getLowerLevelUnit(this.lastColumnUnit.id, this.activeOrder.order.skuId);
      const prevUnitLabelArchive = this.labelArchive.filter(label => label.unitId === prevUnit.id);
      return prevUnitLabelArchive
        .filter(label => label.labelStatus === 'Active' && label.containers.length === 0)
        .length;
    },
    responseTimeWarning() {
      return this.requestResponseTime > 1000;
    },
    renderTimeWarning() {
      return this.renderTime > 500;
    },
    connectionStatusClass() {
      if (!this.connected) return 'error';
      if (this.responseTimeWarning || this.renderTimeWarning) return 'warning';
      return 'connected';
    },
    refreshPercentage() {
      if (this.refreshStatusHide) return 100;
      if (this.refreshStatusFull) return 100;
      return 0;
    },
    activeOrderDateFrom() {
      return this.activeOrder?.order?.startDate;
    },
    last100RequestResponseTimeAverage() {
      return (this.lastResponseTimes.reduce((acc, curr) => acc + curr, 0) / this.lastResponseTimes.length).toFixed(0);
    },
  },
  watch: {
    fetchParams: {
      handler() {
        this.doRefreshInterval(false);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions('labelArchive', [
      'getLabelArchive',
      'getLabelArchiveWithoutActiveParents',
    ]),
    ...mapActions('emptyLabelArchive', [
      'getEmptyLabelArchive',
    ]),
    columnActiveOrder(column) {
      return this.activeOrders.find(o => {
        const labelingPoint = this.labelingPoints.find(lp => lp.unitId === column?.unit?.id
          && lp.id === o.labelingPointId);
        return o.labelingPointId === labelingPoint?.id;
      });
    },
    async fetchLabelArchive(silent = false) {
      if (!this.activeOrder) return [];

      try {
        this.loading = !silent;
        this.silentLoadingLabelArchive = true;
        const labelArchives = await Promise.all(this.fetchParams.map(params => this.getLabelArchive({
          params,
        })));
        const middleColumnsLabelArchives = await Promise.all(this.middleColumnsFetchParams
          .map(params => this.getLabelArchiveWithoutActiveParents({
            params,
          })));
        this.labelArchiveError = false;

        return [
          ...labelArchives?.flatMap(x => x?.data?.items || []),
          ...middleColumnsLabelArchives.flatMap(x => x?.data || []),
        ];
      } catch (error) {
        this.labelArchiveError = true;
        throw error;
      } finally {
        this.loading = false;
        this.silentLoadingLabelArchive = false;
      }
    },
    async fetchEmptyLabelArchive() {
      try {
        this.silentLoadingEmptyLabelArchive = true;
        await this.getEmptyLabelArchive({
          params: {
            plantCode: this.globalSettings.plantCode,
            query: {
              labelStatuses: 'Empty',
            },
          },
        });
        this.emptyLabelArchiveError = false;
      } catch (error) {
        this.emptyLabelArchiveError = true;
        throw error;
      } finally {
        this.silentLoadingEmptyLabelArchive = false;
      }
    },
    async doRefreshInterval(silent = true) {
      this.removeRefreshInterval();
      if (this.stopRefresh) return;

      const start = Date.now();
      let timeElapsed = 0;
      const timeInterval = setInterval(() => {
        timeElapsed += 50;

        if (timeElapsed >= this.requestResponseTime) {
          this.requestResponseTime = timeElapsed;
        }
      }, 50);

      try {
        const [labelArchive] = await Promise.all([
          this.fetchLabelArchive(silent),
          this.fetchEmptyLabelArchive(),
        ]);

        clearInterval(timeInterval);
        this.requestResponseTime = Date.now() - start;
        if (this.lastResponseTimes.length >= 100) {
          this.lastResponseTimes = this.lastResponseTimes.slice(1, 100);
        }
        this.lastResponseTimes = [
          ...this.lastResponseTimes,
          Date.now() - start,
        ];

        const now = Date.now();
        this.labelArchive = labelArchive;

        this.$nextTick(() => {
          this.connected = true;
          this.renderTime = Date.now() - now;
        });
      } catch {
        this.$nextTick(() => {
          clearInterval(timeInterval);
          this.requestResponseTime = 0;
          this.renderTime = 0;
          this.connected = false;
        });
      } finally {
        this.$nextTick(() => {
          const nextIntervalTime = Math.max(this.refreshMaxInterval - this.requestResponseTime - this.renderTime, 5);
          clearInterval(timeInterval);
          this.matryoshkaRefreshInterval = setTimeout(() => {
            this.doRefreshInterval();
          }, nextIntervalTime);
        });
      }
    },
    decrementUntilNext() {
      this.untilNextRefresh = 0;
    },
    removeRefreshInterval() {
      clearInterval(this.matryoshkaRefreshInterval);
    },
  },
  beforeDestroy() {
    this.stopRefresh = true;
    this.removeRefreshInterval();
  },
};
</script>

<template>
  <div class="cockpit-matryoshka">
    <Portal to="right-menu-portal">
      <div
        v-if="!loading"
        class="position-relative refresh-status cursor-pointer"
        :class="connectionStatusClass"
        @click="showPerformanceModal = true"
      >
        <div class="d-flex my-auto text-white">
          <div v-if="debug">
            <i class="fas fa-box fa-xs mr-1" />
            <i
              v-if="labelArchiveError"
              class="fas fa-exclamation-triangle fa-xs fa-fw"
            />
            <i
              v-else-if="silentLoadingLabelArchive"
              class="fas fa-sync-alt fa-xs fa-fw"
            />
            <i
              v-else
              class="fas fa-check fa-xs fa-fw"
            />
          </div>

          <div
            v-if="debug"
            class="mx-3"
          >
            <i class="fas fa-box-open fa-xs mr-1" />
            <i
              v-if="emptyLabelArchiveError"
              class="fas fa-exclamation-triangle fa-xs fa-fw"
            />
            <i
              v-else-if="silentLoadingEmptyLabelArchive"
              class="fas fa-sync-alt fa-xs fa-fw"
            />
            <i
              v-else
              class="fas fa-check fa-xs fa-fw"
            />
          </div>

          <div
            class="ml-3 response-time"
            :class="{
              'text-warning font-weight-bold h5 mb-0': responseTimeWarning,
            }"
          >
            <small v-if="requestResponseTime || connected">
              ~{{ requestResponseTime }}ms
            </small>
            <small v-else>
              Brak połączenia
            </small>

            <i
              class="fas fa-xs fa-fw"
              :class="{
                'fa-wifi-exclamation': responseTimeWarning,
                'fa-wifi': connected && !responseTimeWarning,
                'fa-wifi-slash': !connected,
              }"
            />
          </div>

          <div
            v-if="renderTime"
            class="ml-3 render-time"
            :class="{
              'text-warning font-weight-bold h5 mb-0': renderTimeWarning,
            }"
          >
            <small>
              ~{{ renderTime }}ms
            </small>

            <i class="fas fa-desktop fa-xs fa-fw" />
          </div>
        </div>
      </div>
    </Portal>

    <div
      class="cockpit-matryoshka-columns"
      :class="{
        'flex-column': debug,
        'lines-collapsed': linesCollapsed,
      }"
    >
      <CockpitMatryoshkaColumn
        v-for="(column, index) in columns"
        :key="column.title"
        :title="column.title"
        :active-order="activeOrder"
        :order-instance="columnActiveOrder(column)"
        :unit="column.unit"
        :label-archive="labelArchive"
        :loading="loading"
        :debug-statuses="debugStatuses"
        :open-pallet-items-count="openPalletItemsCount"
        :pallets-column="index === columns.length - 1"
        @show-print-batch="$emit('show-print-batch', $event)"
        @select-item="$emit('select-item', $event)"
        @print-manually="$emit('print-manually', $event)"
      />
    </div>

    <BModal
      v-model="showPerformanceModal"
      title="Performance statistics"
      hide-footer
    >
      <h5>
        {{ requestResponseTime }}ms
      </h5>
      <p>
        UI - API response time (current request)
      </p>

      <hr>

      <h5>
        {{ last100RequestResponseTimeAverage }}ms
      </h5>
      <p>
        UI - API response time (last {{ lastResponseTimes.length }} requests average)
      </p>

      <hr>

      <h5>
        {{ renderTime }}ms
      </h5>
      <p class="mb-0">
        UI frame render time
      </p>
    </BModal>
  </div>
</template>

<style scoped lang="scss">
.cockpit-matryoshka {
  display: flex;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  height: 100%;

  .cockpit-matryoshka-columns {
    height: 100%;
    width: 100%;
    // width: calc(100vw - 192px);
    display: flex;
    gap: 12px;
    overflow-y: auto;

    &.lines-collapsed {
      // width: calc(100vw - 129px);
    }
  }
}

.refresh-status {
  opacity: 0.4;
  margin-right: 1rem;
  margin-left: 2rem;
  position: relative;

  &:hover {
    opacity: 1;
  }

  &.warning {
    opacity: 1;
  }

  &.error {
    background-color: red;
  }

  &.warning,
  &.error {
    opacity: 1;
    padding: 0 0.5rem;
    border-radius: 5px;
    color: white;
  }

  .response-time, .render-time {
    width: 120px;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;

    small {
      margin-bottom: 0;
    }
  }

  .render-time {
    width: 80px;
  }

  .next-refresh-progress {
    position: absolute;
    top: -5px;
    right: 0;
    width: 200px;
    height: 2px;
    background-color: white;
    border-radius: 50px;
    overflow: hidden;

    .next-refresh-progress-bar {
      height: 100%;
      background-color: #d4d4d4;
      transition: opacity 0.5s linear;
      opacity: 1;

      &.hide {
        opacity: 0;
      }
    }
  }
}
</style>
